
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Plus, Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { useQueryClient } from "@tanstack/react-query"
import { useCondominiums } from "@/hooks/useCondominiums"
import { useAuth } from "@/hooks/useAuth"
import { useUnits } from "@/hooks/useUnits"
import { UnitCombobox } from "@/components/ui/unit-combobox"

const NovoMoradorModal = () => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("123456")
  const [fullName, setFullName] = useState("")
  const [phone, setPhone] = useState("")
  const [apartmentNumber, setApartmentNumber] = useState("")
  const [building, setBuilding] = useState("")
  const [role, setRole] = useState("morador")
  const [condominiumId, setCondominiumId] = useState("")
  const [residentType, setResidentType] = useState("owner")
  const [unitId, setUnitId] = useState("")
  
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { data: condominiums } = useCondominiums()
  const { profile } = useAuth()
  const { data: units } = useUnits()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !fullName) {
      toast({
        title: "Erro",
        description: "Email e nome são obrigatórios.",
        variant: "destructive",
      })
      return
    }

    // Se não foi selecionado um condomínio, usar o condomínio do usuário logado
    const finalCondominiumId = condominiumId || profile?.condominium_id

    if (!finalCondominiumId) {
      toast({
        title: "Erro",
        description: "É necessário selecionar um condomínio ou o usuário deve estar associado a um condomínio.",
        variant: "destructive",
      })
      return
    }

    setLoading(true)

    try {
      console.log('Criando morador com dados:', {
        email,
        fullName,
        role,
        condominiumId: finalCondominiumId
      })

      // Usar signup normal em vez de admin.createUser
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role,
            app_code: 'itacondo'
          }
        }
      })

      if (authError) {
        console.error("Erro de autenticação:", authError)
        throw authError
      }

      console.log('Morador criado na auth:', authData.user?.id)

      // Se o usuário foi criado, atualizar o perfil com dados adicionais
      if (authData.user) {
        console.log('Atualizando perfil com dados adicionais...')
        
        // Aguardar um pouco para garantir que o trigger executou
        await new Promise(resolve => setTimeout(resolve, 2000))
        
        const { error: profileError } = await supabase
          .from("profiles")
          .update({
            full_name: fullName,
            phone: phone || null,
            apartment_number: apartmentNumber || null,
            building: building || null,
            role: role as any,
            condominium_id: finalCondominiumId,
            resident_type: residentType as any,
            unit_id: unitId || null
          })
          .eq("id", authData.user.id)

        if (profileError) {
          console.error("Erro ao atualizar perfil:", profileError)
          toast({
            title: "Aviso",
            description: "Morador criado mas houve erro ao configurar dados adicionais: " + profileError.message,
            variant: "destructive",
          })
        } else {
          console.log('Perfil atualizado com sucesso')
        }
      }

      toast({
        title: "Sucesso!",
        description: `Morador criado com sucesso. Senha temporária: ${password}`,
      })

      // Reset form
      setEmail("")
      setPassword("123456")
      setFullName("")
      setPhone("")
      setApartmentNumber("")
      setBuilding("")
      setRole("morador")
      setCondominiumId("")
      setResidentType("owner")
      setUnitId("")
      setOpen(false)

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ["profiles"] })
      queryClient.invalidateQueries({ queryKey: ["stats"] })
    } catch (error: any) {
      console.error("Erro ao criar morador:", error)
      let errorMessage = "Erro ao criar morador"
      
      if (error.message?.includes("User already registered")) {
        errorMessage = "Este email já está cadastrado"
      } else if (error.message?.includes("Password")) {
        errorMessage = "A senha deve ter pelo menos 6 caracteres"
      } else if (error.message?.includes("Signup is disabled")) {
        errorMessage = "Cadastro está desabilitado. Entre em contato com o administrador."
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="ita-button-primary">
          <Plus className="h-4 w-4 mr-2" />
          Novo Morador
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Novo Morador</DialogTitle>
          <DialogDescription>
            Adicione um novo morador ao condomínio.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="password" className="text-right">
                Senha *
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="fullName" className="text-right">
                Nome *
              </Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone" className="text-right">
                Telefone
              </Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="col-span-3"
              />
            </div>
            {profile?.role === 'superadmin' && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="condominium" className="text-right">
                  Condomínio
                </Label>
                <Select value={condominiumId} onValueChange={setCondominiumId}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Selecione um condomínio" />
                  </SelectTrigger>
                  <SelectContent>
                    {condominiums?.map((condominium) => (
                      <SelectItem key={condominium.id} value={condominium.id}>
                        {condominium.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="apartmentNumber" className="text-right">
                Apartamento
              </Label>
              <Input
                id="apartmentNumber"
                value={apartmentNumber}
                onChange={(e) => setApartmentNumber(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="building" className="text-right">
                Bloco
              </Label>
              <Input
                id="building"
                value={building}
                onChange={(e) => setBuilding(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="residentType" className="text-right">
                Tipo de Residente
              </Label>
              <Select value={residentType} onValueChange={setResidentType}>
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Proprietário</SelectItem>
                  <SelectItem value="tenant">Locatário</SelectItem>
                  <SelectItem value="both">Proprietário e Locatário</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="unitId" className="text-right">
                Unidade
              </Label>
              <div className="col-span-3">
                <UnitCombobox
                  units={units || []}
                  value={unitId}
                  onChange={setUnitId}
                  placeholder="Selecionar unidade"
                />
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="role" className="text-right">
                Tipo
              </Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="morador">Morador</SelectItem>
                  <SelectItem value="porteiro">Porteiro</SelectItem>
                  <SelectItem value="sindico">Síndico</SelectItem>
                  <SelectItem value="administracao">Administração</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Criar Morador
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default NovoMoradorModal
